import React, { useState, useEffect } from 'react';
import { IonPage, IonLoading, IonContent } from '@ionic/react';
import { useHistory, useParams } from 'react-router-dom';
import { EditLinkForm } from '../components/Dashboard/Links/EditLinkForm';
import { LinkModel, LinkDestination } from '../types/link';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import { LinksService } from '../api';

interface RouteParams {
  id: string;
}

const EditLink: React.FC = () => {
  const history = useHistory();
  const { id } = useParams<RouteParams>();
  const [link, setLink] = useState<LinkModel | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLink();
  }, [id]);

  const fetchLink = async () => {
    try {
      setLoading(true);
      const data = await LinksService.getLink(id);
      setLink(data);
    } catch (error) {
      console.error('Error fetching link:', error);
      setError('Failed to load link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (
    updatedLink: Partial<LinkModel>, 
    destination: Partial<LinkDestination>,
    file: File | null
  ) => {
    try {
      // Update the link using LinksService
      await LinksService.updateLink(id, updatedLink, destination);

      // If there's a file to upload, handle that separately
      if (file) {
        await LinksService.uploadFile(id, file);
      }

      // Return to links list
      history.push('/links');
    } catch (error) {
      console.error('Error updating link:', error);
      throw error;
    }
  };

  const handleFormCancel = () => {
    history.push('/links');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <IonPage>
          <IonContent>
            <IonLoading isOpen={true} message="Loading link..." />
          </IonContent>
        </IonPage>
      </DashboardLayout>
    );
  }

  if (error || !link) {
    return (
      <DashboardLayout>
        <IonPage>
          <IonContent className="ion-padding">
            <h2>Error</h2>
            <p>{error || 'Link not found'}</p>
            <button onClick={() => history.push('/links')}>Back to Links</button>
          </IonContent>
        </IonPage>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <IonPage>
        <IonContent className="ion-padding">
          <EditLinkForm
            link={link}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
          />
        </IonContent>
      </IonPage>
    </DashboardLayout>
  );
};

export default EditLink; 